import { displayPopover, hidePopover } from './popover.js';

declare global {
  interface Window {
    spriteReady?: Promise<void>;
  }
}
type InternalsLike = Pick<ElementInternals, "setFormValue">;
/**
 * Custom element for cabin picker
 */
export class CabinPicker extends HTMLElement {
    static observedAttributes: string[] = ['circlebehaviour', 'non-subscriber', 'radio'];
    static formAssociated = true;
    #internals: InternalsLike;
    #availableCabins: Cabin[] = [];
    #possibleCabins: Cabin[] = [];
    #onChange?: Function;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });        
        // Feature detection for attachInternals (not available in Safari < 16.4)
        if (typeof this.attachInternals === 'function') {
            this.#internals = this.attachInternals();
        } else {
            // Fallback mock for browsers that don't support ElementInternals API
            this.#internals = {
                setFormValue: () => {},
            };
        }
    }

    attributeChangedCallback(attrName: string, oldValue: string, newValue: string) {
        if (newValue !== oldValue) {
            if (attrName === 'circlebehaviour') {
                this.circlebehaviour = newValue;
            } else if (attrName === 'non-subscriber') {
                this.nonSubscriber = !(newValue === null);
            } else if (attrName === 'radio') {
                this.radio = !(newValue === null);
            }
        }
    }

    connectedCallback() {
        this.addSprites();

        const template = document.getElementById('cabin-picker-template') as HTMLTemplateElement;
        const node = document.importNode(template.content, true);
        this.shadowRoot!.appendChild(node);

        // Set event listeners for all input checkboxes
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            this.setCabinToggleEventListeners(cabinToggle);
            if (this.radio) {
                cabinToggle.addEventListener('click', () => {
                    cabinToggles.forEach((otherCheckbox) => {
                        if (otherCheckbox !== cabinToggle) {
                            (otherCheckbox as HTMLInputElement).checked = false;
                        }
                    });
                });
            }
        });
    }

    disconnectedCallback() {
        this.shadowRoot!.innerHTML = ''; // Clear shadow DOM to remove all event listeners and child elements
    }

    async addSprites() {
        try {
            await window.spriteReady;
        } catch (err) {
            console.error('Failed to load SVG sprites:', err);
            return;
        }

        const sprite = document.querySelector("#icons-sprite");
        if (sprite) {
            const symbols = sprite!.querySelectorAll(
                "#icon-one-cabin, #icon-two-cabin-1, #icon-two-cabin-2, #icon-three-cabin-1, #icon-three-cabin-2, #icon-three-cabin-3, \
                #icon-first, #icon-business, #icon-premium, #icon-economy"
            );
            const nodes = Array.from(symbols).map(symbol => symbol.cloneNode(true));
            this.shadowRoot!.appendChild(document.createElement('svg')).append(...nodes);
        } else {
            console.error("SVG sprite not found in document.");
        }
    }

    addEventListeners(type: string, listener: (ev: Event, cabin: Cabin) => any, options?: boolean | AddEventListenerOptions): void {
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            cabinToggle.addEventListener(type, function(e) {
                listener(e, cabinToggle.value as Cabin);
            }, options);
        });
    }

    get checked(): Cabin[] {
        const checkedCabins = this.shadowRoot!.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
        return Array.from(checkedCabins).map(cabinToggle => cabinToggle.value as Cabin);
    }

    get summary(): FilterSummaryItem[] {
        const checkedCabins = this.shadowRoot!.querySelectorAll('input[type="checkbox"]:checked') as NodeListOf<HTMLInputElement>;
        return Array.from(checkedCabins).map(cabinToggle => {
            const name = (<HTMLLabelElement>cabinToggle.nextElementSibling).innerText;
            const cabin = cabinToggle.value as Cabin;
            const initial = name.trim().charAt(0).toUpperCase() || cabin.charAt(0).toUpperCase();
            return {
                text: initial,
                className: 'cabin-badge ' + cabin,
                title: name
            };
        });
    }

    set checked(cabins: Cabin[]) {
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            cabinToggle.checked = cabins.includes(cabinToggle.value as Cabin);
        });

        this._onChange();
    }

    get possibleCabins(): Cabin[] {
        return this.#possibleCabins;
    }

    set possibleCabins(cabins: Cabin[]) {
        this.#possibleCabins = cabins;
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            let isPossibleCabin = cabins.includes(cabinToggle.value as Cabin);
            let cabinColour = this.shadowRoot!.querySelector('svg .' + cabinToggle.value);
            cabinColour!.classList.toggle("impossible", !isPossibleCabin);
            cabinToggle.parentElement!.classList.toggle('hidden', !isPossibleCabin);
            if (!isPossibleCabin) {
                cabinToggle.checked = false;
            }
        });
        this._update_cabin_svgs();
    }

    get availableCabins(): Cabin[] {
        return this.#availableCabins;
    }

    set availableCabins(cabins: Cabin[]) {
        this.#availableCabins = cabins;
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            let isAvailableCabin = cabins.includes(cabinToggle.value as Cabin);
            let cabinColour = this.shadowRoot!.querySelector('svg .' + cabinToggle.value);
            cabinColour!.classList.toggle("hide", !isAvailableCabin);
            cabinToggle.nextElementSibling!.classList.toggle('disabled', !isAvailableCabin);
            cabinToggle.disabled = false;
            cabinToggle.toggleAttribute('unavailable', !isAvailableCabin);
            if (!isAvailableCabin) {
                cabinToggle.checked = false;
            }
        });
        this._update_cabin_svgs();
    }

    set cabinNames(cabinDisplayNameMapping: CabinDisplayNameMapping) {
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            (<HTMLLabelElement>cabinToggle.nextElementSibling).innerText = cabinDisplayNameMapping[cabinToggle.value as Cabin];
        });
    }

    set onChange(value: Function) {
        if (value) {
            this.#onChange = value;
        } else {
            this.#onChange = undefined;
        }
    }

    get circlebehaviour() {
        return this.getAttribute('circlebehaviour') || '';
    }

    set circlebehaviour(value: string) {
        if (value) {
            this.setAttribute('circlebehaviour', value);
        } else {
            this.removeAttribute('circlebehaviour');
        }
        this._update_cabin_svgs();
    }

    get nonSubscriber(): boolean {
        return this.hasAttribute('non-subscriber');
    }

    set nonSubscriber(value: boolean) {
        if (value) {
            this.setAttribute('non-subscriber', '');
        } else {
            this.removeAttribute('non-subscriber');
        }
    }

    get radio(): boolean {
        return this.hasAttribute('radio');
    }

    set radio(value: boolean) {
        if (value) {
            this.setAttribute('radio', '');
        } else {
            this.removeAttribute('radio')
        }

        const checkboxes = this.shadowRoot!.querySelectorAll('input[type="checkbox"]');
        // When a checkbox is clicked, uncheck all other checkboxes
        checkboxes.forEach((checkbox) => {
            checkbox.addEventListener('click', () => {
                checkboxes.forEach((otherCheckbox) => {
                    if (otherCheckbox !== checkbox) {
                        (otherCheckbox as HTMLInputElement).checked = false;
                    }
                });
            });
        });
    }

    /**
     * Returns the combined value all the checked cabins as a string to make comparisons between cabin pickers easier.
     */
    get comp(): string {
        const cabins = this.checked;
        return cabins.toString();
    }

    _onChange(e?: Event) {
        this._update_cabin_svgs();
        this.#internals.setFormValue(this.radio ? this.checked[0] : this.checked.toString());
        if (this.#onChange && typeof this.#onChange === 'function') {
            this.#onChange(e);
        } else {
            // Default behaviour
        }
    }

    _update_cabin_svgs() {
        const cabinToggles = this.shadowRoot!.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            let cabin = cabinToggle.value as Cabin;
            let cabinColour = this.shadowRoot!.querySelector('svg .' + cabin);
            cabinColour!.classList.toggle("hide", !this.checked.includes(cabin));
        });

        var sections = this.checked.length;
        if (this.circlebehaviour == 'quadrants') {
            sections = 4;
        } else if (this.circlebehaviour == 'possible') {
            sections = this.#possibleCabins.length;
        }

        this.checked.forEach((cab, index) => {
            let path = this.shadowRoot!.querySelector('svg .' + cab);

            if (sections == 1) {
                path!.setAttribute("href", "#icon-one-cabin");
            } else if (sections == 2) {
                path!.setAttribute("href", "#icon-two-cabin-" + (index + 1).toString());
            } else if (sections == 3) {
                path!.setAttribute("href", "#icon-three-cabin-" + (index + 1).toString());
            } else {
                path!.setAttribute("href", "#icon-" + cab);
            }
        });
    }

    reset() {
        // Get all non-hidden, enabled, unchecked cabin toggles and check them
        const cabinToggles = this.shadowRoot!.querySelectorAll('.checkbox:not(.hidden) input[type="checkbox"][name="cabin"]:enabled:not([unavailable]):not(:checked)') as NodeListOf<HTMLInputElement>;
        cabinToggles.forEach(cabinToggle => {
            cabinToggle.checked = true;
        });
    }

    setCabinToggleEventListeners(cabinToggle: HTMLInputElement) {
        cabinToggle.addEventListener('change', this._onChange.bind(this));
        cabinToggle.addEventListener('click', this.preventUncheckingAllCabins);
        cabinToggle.addEventListener('animationend', this.removeCheckBoxAnimations);
        (cabinToggle.parentElement as HTMLDivElement).addEventListener('click', this.animateDisabledCheckbox);
        (cabinToggle.parentElement as HTMLDivElement).addEventListener('mouseover', this.showDisabledCabinTooltip.bind(this));
        (cabinToggle.parentElement as HTMLDivElement).addEventListener('mouseleave', (e: MouseEvent) => {hidePopover();});
    }

    preventUncheckingAllCabins(e: MouseEvent) {
        const target = e.target as HTMLInputElement;
        // If we are unchecking an available cabin and there is only one cabin checked
        if (!target.hasAttribute('unavailable') && !target.checked && target.parentElement!.parentElement!.querySelectorAll('input:not([disabled]):not([unavailable]):checked[type="checkbox"]').length == 0) {
            // Prevent unchecking
            target.classList.add('last-checkbox');
            target.checked = true;
            e.preventDefault();
        }
    }

    removeCheckBoxAnimations(e: AnimationEvent) {
        (<HTMLInputElement>e.target).classList.remove('last-checkbox', 'clicked-disabled');
    }

    animateDisabledCheckbox(e: MouseEvent) {
        const target = e.currentTarget as HTMLElement;
        const checkbox = target.querySelector('input[type="checkbox"]') as HTMLInputElement;
        if (checkbox && checkbox.disabled) {
            checkbox.classList.add('clicked-disabled');
        }
    }

    showDisabledCabinTooltip(e: MouseEvent) {
        const target = e.currentTarget as HTMLElement;
        const checkbox = target.querySelector('input[type="checkbox"]') as HTMLInputElement;
        if (checkbox && (checkbox.disabled || checkbox.hasAttribute('unavailable'))) {
            const createContent = (popover: HTMLDivElement) => {
                if (this.nonSubscriber) {
                    popover.innerHTML = '<label>Subscribe to enable other cabins.</label>';
                } else {
                    popover.innerHTML = '<label>There is currently no reward<br>seat availability for this cabin.</label>';
                }
                return popover;
            }

            displayPopover(checkbox, createContent);

        }
    }
}

customElements.define('cabin-picker', CabinPicker);
